import type { JSX } from '@builder.io/qwik';
import { component$, useContext } from '@builder.io/qwik';
import { cva } from 'class-variance-authority';
import { AppDataContext } from '~/contexts/AppDataContext';
import {
  getCSSColorText,
  getCSSSizeLgText,
  getCSSSizeMdText,
  getCSSSizeSmText,
} from '~/utilities/getCSS';
import { getFontSizeObject } from '~/utilities/getFontSizeObject';
import { getArticleAuthors } from '~/utilities/getArticleAuthors';
import ArticleMobileShareButton from '~/components/Layout/Body/Article/ArticleShare/ArticleMobileShareButton';
import { ArticleContext } from '~/contexts/ArticleContext';
import AuthorImage from '~/components/Shared/AuthorImage/AuthorImage';
import { useIsPremiumTemplate } from '~/utilities/useIsPremiumTemplate';
import { useArticleDate } from '~/utilities/useArticleDate';

const cssClasses = cva('mb-4 flex items-center justify-between', {
  variants: {
    color: getCSSColorText(),
    sizeSm: getCSSSizeSmText(),
    sizeMd: getCSSSizeMdText(),
    sizeLg: getCSSSizeLgText(),
    premiumContainer: {
      true: '!mb-0 md:!mb-4 !justify-center',
      false: '',
    },
  },
});

const authorNameClasses = cva('underline', {
  variants: {
    color: getCSSColorText(),
  },
});

interface ArticleBylineProps {
  byText?: string;
  hasAuthorImage?: boolean;
  orientation?: 'vertical' | 'horizontal';
  includeMobileShare?: boolean;
}

export default component$(
  ({
    byText,
    hasAuthorImage = false,
    orientation,
    includeMobileShare = true,
  }: ArticleBylineProps): JSX.Element => {
    const {
      payloadToSSR: {
        pageContent: { pageSettings },
      },
      themeComponents: { articleTitle },
    } = useContext(AppDataContext);
    const article = useContext(ArticleContext);

    if (!article) return <></>;

    const authors = getArticleAuthors(
      article.authors,
      article.metadata?.hideOwner
    );
    const isPremium = useIsPremiumTemplate();
    const by = () => {
      if (typeof byText === 'string') {
        return byText;
      }

      return pageSettings?.byText?.value || '';
    };

    const dateString = pageSettings?.shouldDisplayUpdatedAtDate?.value
      ? article.updatedAt || ''
      : article.createdAt || '';

    const dateStringFormatted = useArticleDate(dateString);
    return (
      <div
        class={cssClasses({
          color: articleTitle.metaDataColor.value,
          ...getFontSizeObject(articleTitle, 'authorTitleSizeIn', 'Screen'),
          premiumContainer: isPremium,
        })}
      >
        <div class='flex items-center'>
          {!!authors.length && (
            <>
              <span class='mr-1'>{by()}</span>
              {authors.map((each, index) => {
                return (
                  <>
                    {hasAuthorImage && (
                      <AuthorImage
                        image={each.authorInfo.image.value}
                        isRounded
                      />
                    )}
                    <a
                      href={each.link ?? each.pagePath}
                      data-testtype='author-link'
                      class={authorNameClasses({
                        color: isPremium ? 'white' : 'primary',
                      })}
                    >
                      {each.name}
                    </a>
                    {index < authors.length - 1 && ', '}
                  </>
                );
              })}
              <span class='inline-block mx-1'>|</span>
            </>
          )}
          {dateStringFormatted && (
            <time dateTime={dateString}>{dateStringFormatted}</time>
          )}
        </div>
        {!isPremium && includeMobileShare && (
          <ArticleMobileShareButton orientation={orientation} />
        )}
      </div>
    );
  }
);
